/**
 * Control-B — the control panel where a Studio user designs their own private
 * experimental homepage, "Main-B" (Ken 2026-09-22).
 *
 * Each Studio user (Ken, Emerson) gets their own sandbox to try a different
 * look for the homepage — nav colour, a banner, a font, and a custom folder
 * icon — WITHOUT touching the real tenant homepage ("Main-A") that everyone
 * else sees. Settings persist to the caller's own `User.shadow_ui` via
 * /api/shadow-ui/ (self-only), so nobody else — not even the other Studio
 * user — ever sees your Main-B.
 *
 * The preview is a set of WINDOWS, each mounting a REAL slice of the app (e.g.
 * the actual pane-2 card component, `TimelineArtifactRow`) scope-styled by the
 * user's settings — never a hand-drawn mock. (An earlier full-size "Main-B"
 * page that faked the layout was retired for exactly that reason.) Still to
 * come: a folder-view window mounting the real `FolderSidebar`, and swapping
 * the folder icon in that real sidebar via a per-slot hook.
 */
import { useEffect, useRef, useState, type ChangeEvent, type CSSProperties } from 'react'
import { Upload, Loader2, AlertCircle, Check, X } from 'lucide-react'
import { getShadowUi, saveShadowUi, artifacts, type ShadowUi, type Artifact } from '../api'
import { FONTS, useProductName, type FontKey } from '../productName'
import { NAV_COLOR_OPTIONS } from '../navColor'
import { TimelineArtifactRow } from '../components/MatterTimelinePane'

const FOLDER_SLOT = 'folder-icon'

function fileToDataUrl(f: File): Promise<string> {
  return new Promise((res, rej) => {
    const r = new FileReader()
    r.onload = () => (typeof r.result === 'string' ? res(r.result) : rej(new Error('read failed')))
    r.onerror = () => rej(new Error('read failed'))
    r.readAsDataURL(f)
  })
}

export default function ControlB() {
  const product = useProductName()
  const [cfg, setCfg] = useState<ShadowUi>({})
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState('')
  const [saving, setSaving] = useState(false)
  const [savedAt, setSavedAt] = useState<number | null>(null)
  // A few REAL artifacts to render in the "cards" preview window (read-only).
  const [cards, setCards] = useState<Artifact[]>([])
  // Only user edits trigger a save — never the initial server load.
  const dirty = useRef(false)

  useEffect(() => {
    let alive = true
    ;(async () => {
      try {
        const c = await getShadowUi()
        if (alive) setCfg(c || {})
      } catch (e: any) {
        if (alive) setErr(e?.response?.data?.detail || e?.message || 'Failed to load')
      } finally {
        if (alive) setLoading(false)
      }
    })()
    return () => { alive = false }
  }, [])

  // Pull a few real artifacts (read-only) for the "cards" preview window —
  // prefer ones that actually have a summary so the cards look representative.
  useEffect(() => {
    let alive = true
    artifacts.page({ limit: 12, parent: 'null', ignored: 'false' }).then((p) => {
      if (!alive) return
      const rows = p.results || []
      const withSummary = rows.filter((a) => a.email_issue_summary || a.attachment_summary || a.ai_summary)
      setCards((withSummary.length ? withSummary : rows).slice(0, 5))
    }).catch(() => { /* preview is best-effort */ })
    return () => { alive = false }
  }, [])

  // Debounced autosave — instant local preview, quiet persistence. Runs only
  // after a real edit has flipped `dirty`, so opening the page never writes.
  useEffect(() => {
    if (!dirty.current) return
    setSaving(true)
    const t = setTimeout(async () => {
      try { await saveShadowUi(cfg); setSavedAt(Date.now()) }
      catch (e: any) { setErr(e?.response?.data?.detail || e?.message || 'Save failed') }
      finally { setSaving(false) }
    }, 600)
    return () => clearTimeout(t)
  }, [cfg])

  function patch(p: Partial<ShadowUi>) { dirty.current = true; setCfg((c) => ({ ...c, ...p })) }

  async function onImage(e: ChangeEvent<HTMLInputElement>, maxBytes: number, apply: (dataUrl: string, name: string) => void) {
    const f = e.target.files?.[0]; e.target.value = ''
    if (!f) return
    setErr('')
    if (!/^image\//.test(f.type)) { setErr('Please choose an image file.'); return }
    if (f.size > maxBytes) { setErr(`Image is over ${Math.round(maxBytes / 1_000_000)} MB — use a smaller one.`); return }
    try { apply(await fileToDataUrl(f), f.name) }
    catch (e2: any) { setErr(e2?.message || 'Could not read image') }
  }
  function setFolderIcon(data: string, filename: string) {
    dirty.current = true
    setCfg((c) => ({ ...c, slots: { ...(c.slots || {}), [FOLDER_SLOT]: { type: 'image', data, filename } } }))
  }
  function clearFolderIcon() {
    dirty.current = true
    setCfg((c) => { const s = { ...(c.slots || {}) }; delete s[FOLDER_SLOT]; return { ...c, slots: s } })
  }

  const folderImg = cfg.slots?.[FOLDER_SLOT]?.data
  const fontKey = (cfg.font && cfg.font in FONTS ? cfg.font : product.brandFont) as FontKey
  const fontFamily = FONTS[fontKey].family
  const navColor = cfg.nav_color || '#E8E4DD'
  // Scope the chosen font onto real components inside a preview window: set the
  // wrapper font-family (for inheriting text) AND the --me-font-* vars (for
  // text that pins its family to a var). Descendants inherit both.
  const previewVars = {
    fontFamily,
    '--me-font-summary': fontFamily,
    '--me-font-record': fontFamily,
    '--me-font-chrome': fontFamily,
    '--me-font-data': fontFamily,
  } as CSSProperties

  const savedLabel = saving ? 'Saving…' : savedAt ? 'Saved' : ''

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center text-[12px] me-text-mute">
        <Loader2 size={14} className="mr-2 animate-spin" /> Loading Main-B…
      </div>
    )
  }

  return (
    <div className="h-full overflow-y-auto me-scroll">
      <div className="mx-auto max-w-5xl p-6">
        <div className="mb-1 flex items-center gap-3">
          <h1 className="text-[18px] font-semibold me-text">Control-B — design your private Main-B homepage</h1>
          {savedLabel && (
            <span className="inline-flex items-center gap-1 text-[11px] me-text-mute">
              {saving ? <Loader2 size={11} className="animate-spin" /> : <Check size={11} />} {savedLabel}
            </span>
          )}
        </div>
        <p className="mb-6 text-[12px] me-text-mute">
          Try a different look for the homepage — nav colour, a banner, a font, a custom folder icon.
          Everything here is <span className="me-text">yours alone</span>: it never changes the real
          homepage, and no one else (not even the other Studio user) sees it. Changes save automatically
          and show live in the preview on the right.
        </p>

        {err && (
          <div className="mb-4 flex items-center gap-2 text-[12px] text-[color:var(--me-urgent)]">
            <AlertCircle size={13} /> {err}
          </div>
        )}

        <div className="grid gap-6 md:grid-cols-2">
          {/* ── Controls ── */}
          <div className="space-y-6">
            {/* Nav colour */}
            <section>
              <div className="mb-2 text-[11px] font-medium uppercase tracking-wider me-text-mute">Control-bar colour</div>
              <div className="flex flex-wrap gap-1.5">
                {[...NAV_COLOR_OPTIONS.earthy, ...NAV_COLOR_OPTIONS.neon].map((c) => (
                  <button key={c} onClick={() => patch({ nav_color: c })} title={c}
                    className="h-6 w-6 rounded border"
                    style={{ backgroundColor: c, borderColor: cfg.nav_color === c ? 'var(--me-text)' : 'var(--me-border)', outline: cfg.nav_color === c ? '2px solid var(--me-text)' : 'none' }} />
                ))}
                <label className="ml-1 inline-flex items-center gap-1 text-[11px] me-text-mute">
                  custom
                  <input type="color" value={/^#[0-9a-fA-F]{6}$/.test(navColor) ? navColor : '#E8E4DD'}
                    onChange={(e) => patch({ nav_color: e.target.value })} className="h-6 w-8 cursor-pointer bg-transparent" />
                </label>
              </div>
            </section>

            {/* Font */}
            <section>
              <div className="mb-2 text-[11px] font-medium uppercase tracking-wider me-text-mute">Font</div>
              <select value={fontKey} onChange={(e) => patch({ font: e.target.value })}
                className="me-input" style={{ maxWidth: 260, fontFamily }}>
                {(Object.keys(FONTS) as FontKey[]).map((k) => (
                  <option key={k} value={k} style={{ fontFamily: FONTS[k].family }}>{FONTS[k].label}</option>
                ))}
              </select>
            </section>

            {/* Banner */}
            <section>
              <div className="mb-2 text-[11px] font-medium uppercase tracking-wider me-text-mute">Banner (behind the control bar)</div>
              <div className="flex items-center gap-2">
                <label className="me-btn me-btn-outline flex cursor-pointer items-center gap-1.5 text-[12px]">
                  <Upload size={13} /> Upload
                  <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden"
                    onChange={(e) => onImage(e, 2_000_000, (data) => patch({ nav_banner: data }))} />
                </label>
                {cfg.nav_banner && (
                  <button onClick={() => patch({ nav_banner: undefined })}
                    className="me-btn me-btn-outline text-[12px] flex items-center gap-1"><X size={12} /> Remove</button>
                )}
              </div>
            </section>

            {/* Folder icon slot */}
            <section>
              <div className="mb-2 text-[11px] font-medium uppercase tracking-wider me-text-mute">Folder icon</div>
              <p className="mb-2 text-[11px] me-text-mute">Replace the little folder symbol with your own picture.</p>
              <div className="flex items-center gap-2">
                <label className="me-btn me-btn-outline flex cursor-pointer items-center gap-1.5 text-[12px]">
                  <Upload size={13} /> Upload
                  <input type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="hidden"
                    onChange={(e) => onImage(e, 1_000_000, setFolderIcon)} />
                </label>
                {folderImg && (
                  <>
                    <img src={folderImg} alt="folder" className="h-6 w-6 rounded object-cover" />
                    <button onClick={clearFolderIcon}
                      className="me-btn me-btn-outline text-[12px] flex items-center gap-1"><X size={12} /> Remove</button>
                  </>
                )}
              </div>
            </section>
          </div>

          {/* ── Preview windows: each shows a REAL slice of the app wearing your look ── */}
          <div>
            <div className="mb-2 text-[11px] font-medium uppercase tracking-wider me-text-mute">Preview windows</div>
            <div className="space-y-4">

              {/* Window — control bar */}
              <div>
                <div className="mb-1 text-[10px] uppercase tracking-wider me-text-mute">Control bar</div>
                <div className="overflow-hidden rounded-lg border me-border">
                  <div className="flex h-12 items-center gap-4 px-3"
                    style={{
                      backgroundColor: navColor,
                      backgroundImage: cfg.nav_banner ? `url("${cfg.nav_banner}")` : undefined,
                      backgroundSize: 'cover', backgroundPosition: 'center',
                    }}>
                    <span className="whitespace-nowrap text-[16px] leading-none" style={{ fontFamily, color: '#14110E' }}>
                      {product.name || 'Matters'}
                    </span>
                    <span className="text-[13px] leading-none opacity-80" style={{ fontFamily, color: '#14110E' }}>
                      Main · Playbook · Stats
                    </span>
                  </div>
                </div>
              </div>

              {/* Window — the REAL pane-2 cards (issue & attachment summaries) */}
              <div>
                <div className="mb-1 flex items-center justify-between text-[10px] uppercase tracking-wider me-text-mute">
                  <span>Cards — issue &amp; attachment summaries</span>
                  <span className="rounded bg-[color:var(--me-panel)] px-1 py-0.5 normal-case">real component</span>
                </div>
                <div className="overflow-hidden rounded-lg border me-border bg-[color:var(--me-surface)] p-2" style={previewVars}>
                  {cards.length === 0 ? (
                    <div className="flex items-center gap-2 p-3 text-[12px] me-text-mute">
                      <Loader2 size={12} className="animate-spin" /> Loading real cards…
                    </div>
                  ) : (
                    // pointer-events off — this is a look-only preview of the live
                    // component; never let a click here edit/navigate.
                    <div style={{ pointerEvents: 'none' }}>
                      {cards.map((a) => (
                        <TimelineArtifactRow key={a.id} artifact={a} selected={false} onSelect={() => {}} showChips showDetails />
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Window — folder view (real sidebar, next) */}
              <div>
                <div className="mb-1 text-[10px] uppercase tracking-wider me-text-mute">Folder view</div>
                <div className="rounded-lg border border-dashed me-border p-3 text-[12px] me-text-mute">
                  The real folder sidebar — with your custom folder icon — mounts here next.
                </div>
              </div>

            </div>
            <p className="mt-3 text-[11px] me-text-mute">
              Each window is a real piece of the app wearing your settings — not a mock-up. More
              windows (folder view, and others you name) to come.
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
